import React, { useState, useMemo, useEffect, useContext } from 'react';
import ReactDOMServer from 'react-dom/server';
import { AuthContext } from '../../context/AuthContext';
import api from '../../api/axios';
import {
  AutoComplete, Input, Table, Button, Space, InputNumber, Form,
  Select, Switch, Divider, Row, Col, Typography, message, Card, Statistic, Empty
} from 'antd';
import {
  SearchOutlined, PlusOutlined, DeleteOutlined, DollarCircleOutlined,
  BankOutlined, CreditCardOutlined, PrinterOutlined
} from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';

const { Text, Paragraph } = Typography;
const { Option } = Select;

// --- Printable Invoice ---
const PrintableInvoice = ({ data }) => {
  const { cart = [], financials = {}, customer = {}, refNo, fsNo, date, shop = {} } = data || {};
  const { subtotal = 0, discount = 0, withholdingAmount = 0, vatAmount = 0, grandTotal = 0 } = financials;

  const numberToWords = (num) => {
    if (num === 0) return 'Zero';
    
    const belowTwenty = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten",
      "Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
    const tens = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
    const thousands = ["","Thousand","Million","Billion"];

    const helper = (n) => {
      if (n === 0) return "";
      else if (n < 20) return belowTwenty[n] + " ";
      else if (n < 100) return tens[Math.floor(n/10)] + " " + helper(n%10);
      else return belowTwenty[Math.floor(n/100)] + " Hundred " + helper(n%100);
    }

    let word = "";
    let i = 0;
    while(num > 0){
      if(num % 1000 !== 0){
        word = helper(num % 1000) + thousands[i] + " " + word;
      }
      num = Math.floor(num / 1000);
      i++;
    }
    return word.trim();
  }

  const watermarkStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(-45deg)',
    fontSize: '120px',
    fontFamily: 'Arial, sans-serif',
    color: 'rgba(40, 38, 38, 0.2)',
    zIndex: 0,
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
  };

  return (
    <html lang="en">
      <head>
        <title>Invoice</title>
        <style>{`
          @page { size: A4; margin: 5mm; }
          body { font-family: "Times New Roman", serif; font-size: 13px; margin: -10; padding: 0; position: relative; }
          .container { padding: 40px; position: relative; min-height: 297mm; }
           .topper {
    position: absolute;
    top: 5mm;
    right: 5mm;
     margin-buttom:20mm,
    font-size: 10px;
    text-align: right;
    width: 100%;
  }
          .header { font-size: 28px; font-weight: bold; text-align: left; margin: 0; margin-top:20px;  text-transform: uppercase;}
          .header2 { font-size: 14px;font-weight: bold;  text-align: left; margin: 0; text-transform: uppercase; }
          .title { font-size: 28px; text-align: center; margin: 15px 0; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 14px; }
          th, td { border: 1px solid #000; padding: 5px; }
          .totals-table { width: 40%; float: right; margin-top: 5px; border-collapse: collapse; }
          .totals-table td { border: 1px solid #000; padding: 5px; text-align: left; }
          .footer { position: absolute; bottom: 60px; width: 100%; font-size: 14px; }
          .customer-table { width: 100%; border-collapse: separate; border-spacing: 2px; }
          .customer-table td { border: 1px solid #000; padding: 3px; font-size: 14px; }
        `}</style>
      </head>
      <body>
        <div className="container">
          <div style={watermarkStyle}>Attachement</div>
          <p className='topper'>{uuidv4()} | {new Date().toLocaleString('en-GB', { hour12: false })}</p>

          {/* Header */}
          <p className="header">{shop.header1 || 'SHOP NAME'}</p>
          <p className="header2">{shop.header2 || 'SHOP ADDRESS'}</p>
          <p className="header2">{shop.header3 || 'PHONE'}</p>
          <p className="header2">{shop.header4 || 'TIN'}</p>
            <p className="header2">{shop.header5 || 'TIN'}</p>

          {/* Title */}
          <h2 className="title">Cash Sales Attachment</h2>

          {/* Customer Info */}
          <table className="customer-table">
            <tbody>
              <tr>
                <td>Date:</td>
                <td>{date ? new Date(date).getMonth()+1 + '/' + new Date(date).getDate() + '/' + new Date(date).getFullYear() : 
       new Date().getMonth()+1 + '/' + new Date().getDate() + '/' + new Date().getFullYear()}</td>

                <td>Reference:</td>
                <td>CSI-ST<sub>1-01-{refNo || ''}</sub></td>
              </tr>
              <tr>
                <td>Bill To:</td>
                <td>{customer.name || ''}</td>
                <td>FS NO:</td>
                <td>{fsNo || ''}</td>
              </tr>
              <tr>
                <td>TIN:</td>
                <td>{customer.tin || ''}</td>
               
              </tr>
              <tr>
                 <td>Address:</td>
                <td>{customer.address || ''}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ height: '20px' }}></div>

          {/* Items Table */}
          <table>
            <thead style={{fontSize:'18px'}}>
              <tr>
                <th>ID</th>
                <th>Description</th>
                <th>Unit</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Line Total</th>
              </tr>
            </thead>
            <tbody>
              {cart.map(item => (
                <tr key={item.key}>
                  <td>{item.partNumber}</td>
                  <td>{item.partName}</td>
                  <td>{item.measurementUnit || ''}</td>
                  <td style={{ textAlign: 'right' }}>{item.quantity}</td>
                  <td style={{ textAlign: 'right' }}>{item.price.toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }}>{(item.price * item.quantity).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ height: '30px' }}></div>

          {/* Totals Table */}
          <table className="totals-table">
            <tbody>
              <tr>
                <td>Subtotal:</td>
                <td style={{ textAlign: 'right' }}>{subtotal.toFixed(2)}</td>
              </tr>
              {vatAmount > 0 && (
                <tr>
                  <td>VAT:</td>
                  <td style={{ textAlign: 'right' }}>{vatAmount.toFixed(2)}</td>
                </tr>
              )}
              {withholdingAmount > 0 && (
                <tr>
                  <td>Withholding:</td>
                  <td style={{ textAlign: 'right' }}>{withholdingAmount.toFixed(2)}</td>
                </tr>
              )}
              <tr>
                <td><strong>Grand Total:</strong></td>
                <td style={{ textAlign: 'right' }}><strong>{grandTotal.toFixed(2)}</strong></td>
              </tr>
            </tbody>
          </table>

            <div style={{ float: 'left', marginTop: '45px'}}>
            Payment Method - <br></br>
            Amount in Words: {numberToWords(Math.round(grandTotal))} Only
          </div>

          {/* Footer */}
          <div className="footer">
            <p>Prepared by: m</p>
            <p>{shop.footer1}</p>
            <p>Received by: Name ________________________ Signature _________________________</p>
            <p><strong>NOT VALID WITHOUT FISCAL RECEIPT ATTACHED</strong></p>
          </div>
        </div>
      </body>
    </html>
  );
};



// --- Main Component ---
const NewSale = () => {
  const { user } = useContext(AuthContext);
  const userShopId = user?.shopId;
    const userWareId = user?.warehouseId;

  const [form] = Form.useForm();
  const [cart, setCart] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [paymentType, setPaymentType] = useState('cash');
  const [messageApi,contextHolder] = message.useMessage();
  const [vatEnabled, setVatEnabled] = useState(true);
const [withholdingEnabled, setWithholdingEnabled] = useState(true);
const [shopInfo, setShopInfo] = useState(null); // <-- add state for shop info

  // --- Fetch data ---
  useEffect(() => {
    console.log(userShopId);
   console.log(userWareId);
    if (!userShopId) return;
  api.get(`/api/inventories?wareId=${userWareId}`)
      .then(res => setInventory(res.data))
      .catch(console.error);

    api.get('/api/sales/customers')
      .then(res => setCustomers(res.data))
      .catch(console.error);
      api.get(`/api/shops/${userShopId}/info`)
    .then(res => setShopInfo(res.data))
    .catch(console.error);
    api.get('/api/sales/banks')
      .then(res => setBanks(res.data))
      .catch(console.error);
  }, [userShopId,userWareId]);

  // --- Totals ---
  const subtotal = useMemo(() => cart.reduce((acc, i) => acc + i.price * i.quantity, 0), [cart]);
const discount = Form.useWatch('discount', form) || 0;
const subtotalAfterDiscount = subtotal - discount;

// Withholding only if > 20,000
// Withholding only if enabled and subtotalAfterDiscount > 20,000
const withholdingAmount = withholdingEnabled && subtotalAfterDiscount > 20000 
  ? subtotalAfterDiscount * 0.03 
  : 0;

// VAT only if enabled
const vatAmount = vatEnabled 
  ? (subtotalAfterDiscount - withholdingAmount) * 0.15 
  : 0;

// Grand total
const grandTotal = subtotalAfterDiscount - withholdingAmount + vatAmount;



  // --- Cart functions ---
  const handleAddToCart = () => {
    const quantity = form.getFieldValue('quantity');
    if (!selectedPart || !quantity || quantity <= 0) return message.error('Select part and quantity');
    if (cart.some(i => i.id === selectedPart.id)) return message.warning('Already in cart');
    setCart([...cart, { ...selectedPart, quantity, key: selectedPart.id,  buyingPrice: selectedPart.buyingPrice || 0,  // default 0
  price: selectedPart.price || 0      }]);
    setSelectedPart(null);
    form.resetFields(['search', 'quantity']);
  };

  const handleRemoveFromCart = id => setCart(cart.filter(i => i.id !== id));
  const handleQuantityChange = (id, qty) => setCart(cart.map(i => i.id === id ? { ...i, quantity: qty } : i));

  // --- Submit sale ---
const handleSubmit = async (values) => {
  if (!cart.length) return messageApi.error('Cart empty');
  if (!userShopId) return messageApi.error('User shop not found');

  const payload = {
    shopId: userShopId,
    warehouseId: userWareId,
    customerName: values.customerName,
    customerTin: values.customerTin,
      customerPhone: values.customerPhone || null,
    refNo: values.refNo,
fsNo: values.fsNo,
remark: values.remark || null,
    paymentType: values.paymentType,
    cashAmount: values.cashAmount || 0,
    creditAmount: values.creditAmount || 0,
    bank1Id: values.bank1Account || null,
    bank1Amount: values.bank1Amount || 0,
    bank2Id: values.bank2Account || null,
    bank2Amount: values.bank2Amount || 0,
    discount: values.discount || 0,
    vatApplied: vatEnabled,
    withholdingApplied: withholdingEnabled,
    subtotal,
    vatAmount,
    withholdingAmount,
    grandTotal,
   items: cart.map(i => ({
  name: i.partName ? `${i.partName} / ${i.partNumber || ''}` : i.partName, // name/partnumber combo
  quantity: i.quantity,
  buyingPrice: i.buyingPrice || 0,
  unitPrice: i.price,
  measurementUnit: i.measureUnit || null,
  lineTotal: i.price * i.quantity,
  warehouseId: i.warehouseId || userWareId,
  isInventoryItem: !!i.id, // optional flag to know if this is a real inventory item
})),

  };

  try {
    const res = await api.post('/api/sales/createbuyandsale', payload);

  



await api.post('/api/attachment/save', {
  customerName: values.customerName,
  customerTIN: values.customerTin,

  salesDate: new Date().toISOString().split('T')[0], // DATEONLY
  salesTime: new Date().toTimeString().split(' ')[0], // STRING

  shopInfoId: shopInfo.id,
  remark: values.remark || null,

  grandTotal,
  vatAmount,

  itemCount: cart.reduce((t, i) => t + i.quantity, 0),

  fsNo: values.fsNo,
  refNo: values.refNo,

  attachmentItems: cart.map(i => ({
    partname:  i.partName ? `${i.partName} / ${i.partNumber || ''}` : i.partName, 
    partnumber:  i.partName ? `${i.partName} / ${i.partNumber || ''}` : i.partName, 
    measureunit: i.measureUnit || '',
    quantity: i.quantity,
    price: i.price,
    lineTotal: i.price * i.quantity,
  })),
});

      messageApi.success('Sale created successfully');
       messageApi.success('Attachment saved successfully');
    setCart([]);
    form.resetFields();
    form.setFieldsValue({ paymentType: 'cash' });
    setPaymentType('cash');
  } catch (err) {
    messageApi.error(err.response?.data?.message || err.message || 'Failed to create sale');
  }
};



  // --- Search options ---
const searchOptions = useMemo(() => {
  return inventory.map(i => {
    const importerName = i.sparepart.importerId || 'Unknown'; // replace with real importer lookup if needed
    const label = `[${i.sparepart.importerId}] ${importerName} - ${i.sparepart.partNumber}`;
    return {
      value: label,
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{i.sparepart.importerId} {i.sparepart.partName} <Text type="secondary">({i.sparepart.partNumber})</Text></span>
          <Text strong>${i.sparepart.price?.toFixed(2) || 0}</Text>
        </div>
      ),
      part: i.sparepart,
    };
  });
}, [inventory]);



  // --- Cart table columns ---
const cartColumns = [
  { title: 'Part', dataIndex: 'partName', render: (t, r) => <>{t} <Text type="secondary">{r.partNumber}</Text></> },
  { 
    title: 'Buying Price',
    dataIndex: 'buyingPrice',
    render: (p, r) => (
      <InputNumber 
        min={0} 
        value={p} 
        onChange={(val) => setCart(cart.map(i => i.id === r.id ? { ...i, buyingPrice: val } : i))}
        formatter={v => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        parser={v => v.replace(/\$\s?|(,*)/g, '')}
        style={{ width: '100%' }}
      />
    ),
  },
  { 
    title: 'Selling Price',
    dataIndex: 'price',
    render: (p, r) => (
      <InputNumber 
        min={0} 
        value={p} 
        onChange={(val) => setCart(cart.map(i => i.id === r.id ? { ...i, price: val } : i))}
        formatter={v => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        parser={v => v.replace(/\$\s?|(,*)/g, '')}
        style={{ width: '100%' }}
      />
    ),
  },
  { title: 'Qty', dataIndex: 'quantity', render: (q, r) => <InputNumber min={1} value={q} onChange={(val) => handleQuantityChange(r.id, val)} style={{ width: '100%' }} /> },
  { title: 'Total', render: (_, r) => `$${((r.price ?? 0) * r.quantity).toFixed(2)}` },
  {
  title: 'Profit',
  render: (_, r) => `$${((r.price - r.buyingPrice) * r.quantity).toFixed(2)}`
}
,
  { title: 'Action', render: (_, r) => <Button danger type="text" icon={<DeleteOutlined />} onClick={() => handleRemoveFromCart(r.id)} /> }
];



  const commonInputProps = { style: { width: '100%' }, min: 0, formatter: v => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','), parser: v => v.replace(/\$\s?|(,*)/g, '') };

  const renderPaymentFields = () => {
    switch (paymentType) {
      case 'cash': return <Paragraph>Pay <Text strong>${grandTotal.toFixed(2)}</Text> in cash.</Paragraph>;
      case 'credit': return <Paragraph>Pay <Text strong>${grandTotal.toFixed(2)}</Text> on credit.</Paragraph>;
      case 'bank': return (<Form.Item name="bank1Account" label="Bank Account" rules={[{ required: true }]}>
        <Select placeholder="Select Bank">{banks.map(b => <Option key={b.id} value={b.id}>{b.name}_{b.accountNumber}</Option>)}</Select>
      </Form.Item>);
      case 'cash+bank': return (<Row gutter={8}>
        <Col span={8}><Form.Item name="cashAmount" label="Cash" rules={[{ required: true }]}><InputNumber {...commonInputProps} /></Form.Item></Col>
        <Col span={8}><Form.Item name="bank1Account" label="Bank" rules={[{ required: true }]}><Select>{banks.map(b => <Option key={b.id} value={b.id}>{b.name}_{b.accountNumber}</Option>)}</Select></Form.Item></Col>
        <Col span={8}><Form.Item name="bank1Amount" label="Bank Amount" rules={[{ required: true }]}><InputNumber {...commonInputProps} /></Form.Item></Col>
      </Row>);
      case 'bank+bank': return (<>
        <Row gutter={8}>
          <Col span={12}><Form.Item name="bank1Account" label="Bank 1" rules={[{ required: true }]}><Select>{banks.map(b => <Option key={b.id} value={b.id}>{b.name}_{b.accountNumber}</Option>)}</Select></Form.Item></Col>
          <Col span={12}><Form.Item name="bank1Amount" label="Amount 1" rules={[{ required: true }]}><InputNumber {...commonInputProps} /></Form.Item></Col>
        </Row>
        <Row gutter={8}>
          <Col span={12}><Form.Item name="bank2Account" label="Bank 2" rules={[{ required: true }]}><Select>{banks.map(b => <Option key={b.id} value={b.id}>{b.name}_{b.accountNumber}</Option>)}</Select></Form.Item></Col>
          <Col span={12}><Form.Item name="bank2Amount" label="Amount 2" rules={[{ required: true }]}><InputNumber {...commonInputProps} /></Form.Item></Col>
        </Row>
      </>);
      default: return null;
    }
  };

  const handlePrint = () => {
    if (!cart.length) return message.error('Cart empty');
    const allValues = form.getFieldsValue();
    const printData = {
      cart,
      financials: { subtotal, discount, withholdingAmount, vatAmount, grandTotal },
      customer: { name: allValues.customerName, tin: allValues.customerTin },
    };
    const htmlString = ReactDOMServer.renderToStaticMarkup(<PrintableInvoice data={printData} />);
    const w = window.open('', '_blank', 'width=800,height=600');
    if (w) { w.document.write(htmlString); w.document.close(); w.print(); }
    else message.error('Pop-up blocked');
  };

  return (
    <div style={{ padding: 16, background: '#f0f2f5' }}>
      {contextHolder}
      <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ quantity: 1, discount: 0, paymentType: 'cash' }}>
        <Row gutter={16}>
          <Col xs={24} lg={14}>
            <Card title="Add Products & Review Cart" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={8} align="bottom">
                <Col flex="auto">
                  <Form.Item name="search" label="Search Part / Write if not listed name with part number">
  <AutoComplete
    options={searchOptions}
    onSelect={(value, option) => {
      setSelectedPart(option.part);
      form.setFieldsValue({ quantity: 1, partName: option.part.partName });
    }}
    onSearch={(value) => {
      // Allow typing free text: set as selectedPart temporarily
      if (value && !searchOptions.some(o => o.value === value)) {
        setSelectedPart({ partName: value, id: uuidv4(), price: 0, buyingPrice: 0 });
      }
    }}
    filterOption={(input, option) => {
      const part = option.part;
      return (
        part.partName.toLowerCase().includes(input.toLowerCase()) ||
        part.partNumber?.toLowerCase().includes(input.toLowerCase()) ||
        String(part.importerId || '').includes(input)
      );
    }}
    // Allow user to type anything
    allowClear
    placeholder="Search or type new part name"
  >
    <Input suffix={<SearchOutlined />} />
  </AutoComplete>
</Form.Item>

                </Col>
                <Col style={{ width: '90px' }}>
                  <Form.Item name="quantity" label="Qty">
                    <InputNumber min={1} style={{ width: '100%' }} disabled={!selectedPart} />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item label=" ">
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddToCart} disabled={!selectedPart}>Add</Button>
                  </Form.Item>
                </Col>
              </Row>
              <Table columns={cartColumns} dataSource={cart} pagination={false} size="small" locale={{ emptyText: <Empty description="Cart empty" /> }} scroll={{ y: 240 }} />
            </Card>

           <Card title="Customer & Payment" size="small">
  <Row gutter={[8, 8]} wrap>
    {/* Customer Name */}
    <Col xs={24} sm={8} md={6}>
      <Form.Item
        name="customerName"
        label="Customer"
        rules={[{ required: true }]}
      >
        <AutoComplete
          options={customers.map(c => ({ value: c.name, tin: c.tin }))}
          placeholder="Customer Name"
          onSelect={(val, option) =>
            form.setFieldsValue({ customerTin: option?.tin || '' })
          }
          filterOption={(input, option) =>
            option.value.toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: '100%' }}
        />
      </Form.Item>
    </Col>

    {/* Customer TIN */}
    <Col xs={24} sm={6} md={4}>
      <Form.Item name="customerTin" label="TIN">
        <Input placeholder="Optional" />
      </Form.Item>
    </Col>

    {/* Customer Phone */}
    <Col xs={24} sm={6} md={4}>
      <Form.Item name="customerPhone" label="Phone">
        <Input placeholder="Optional" />
      </Form.Item>
    </Col>

    {/* Reference No */}
    <Col xs={24} sm={6} md={4}>
      <Form.Item name="refNo" label="Ref No">
        <Input placeholder="Ref No" />
      </Form.Item>
    </Col>

    {/* FS No */}
    <Col xs={24} sm={6} md={4}>
      <Form.Item name="fsNo" label="FS No">
        <Input placeholder="FS No" />
      </Form.Item>
    </Col>


    {/* Payment Type */}
    <Col xs={24} sm={8} md={6}>
      <Form.Item
        name="paymentType"
        label="Payment"
        rules={[{ required: true }]}
      >
        <Select onChange={setPaymentType} placeholder="Select Method">
          <Option value="cash"><Space><DollarCircleOutlined />Cash</Space></Option>
          <Option value="credit"><Space><CreditCardOutlined />Credit</Space></Option>
          <Option value="bank"><Space><BankOutlined />Bank</Space></Option>
          <Option value="cash+bank"><Space><DollarCircleOutlined /><BankOutlined />Cash+Bank</Space></Option>
          <Option value="bank+bank"><Space><BankOutlined /><BankOutlined />Bank+Bank</Space></Option>
        </Select>
      </Form.Item>
    </Col>
  </Row>

  {/* Dynamic payment fields (cash, bank, etc.) */}
  <Row gutter={[8, 8]}>
    <Col span={24}>
      {renderPaymentFields()}
    </Col>
  </Row>
  {/* Remark */}
<Col xs={24} sm={24} md={24}>
  <Form.Item name="remark" label="Remark / Note">
    <Input.TextArea
      placeholder="Add any additional notes (optional)"
      rows={2}
      maxLength={255}
      showCount
    />
  </Form.Item>
</Col>
</Card>

          </Col>

          <Col xs={24} lg={10}>
            <Card title="Summary & Actions" size="small">
             <Row gutter={16} align="middle">
  <Col>
    <Form.Item name="discount" label="Discount" style={{ marginBottom: 0 }}>
      <InputNumber {...commonInputProps} style={{ width: 120 }} />
    </Form.Item>
  </Col>

  <Col>
    <Form.Item label="V" style={{ marginBottom: 0 }}>
      <Switch checked={vatEnabled} onChange={setVatEnabled} />
    </Form.Item>
  </Col>

  <Col>
    <Form.Item label="Withholding" style={{ marginBottom: 0 }}>
      <Switch checked={withholdingEnabled} onChange={setWithholdingEnabled} />
    </Form.Item>
  </Col>
</Row>

              
              <Divider />
               <div style={{ fontSize: '12px', lineHeight: '1.6em' }}>
      <Row justify="space-between">
        <Col>Subtotal:</Col>
        <Col><strong>${subtotal.toFixed(2)}</strong></Col>
      </Row>

      {withholdingEnabled && subtotalAfterDiscount > 20000 && (
        <Row justify="space-between">
          <Col>Withholding (3%):</Col>
          <Col><strong>${withholdingAmount.toFixed(2)}</strong></Col>
        </Row>
      )}

      {vatEnabled && (
        <Row justify="space-between">
          <Col>VAT (15%):</Col>
          <Col><strong>${vatAmount.toFixed(2)}</strong></Col>
        </Row>
      )}

      <Divider style={{ margin: '8px 0' }} />

      <Row justify="space-between">
        <Col><strong>Grand Total:</strong></Col>
        <Col>
          <strong style={{ fontSize: '14px', color: '#1890ff' }}>
            ${grandTotal.toFixed(2)}
          </strong>
        </Col>
      </Row>
    </div>

              <Divider />
              <Space>
                {/* <Button icon={<PrinterOutlined />} onClick={handlePrint}>Print</Button> */}
                <Button type="primary" htmlType="submit">Complete Sale</Button>
              </Space>
            </Card>
          </Col>  

        </Row>
      </Form>
    </div>
  );
};

export default NewSale;
